import { useState } from "react";
import { ExternalLink, MapPin, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/Button";
import type { ApplicationRoadData, OfficialApplicationDetail } from "../../types";
import { InfoHint } from "./InfoHint";

// Strona GDDKiA, z której pochodzą dane (i na której można ręcznie sprawdzić
// kilometraż) — link wymagany przez zgłoszenie DEV „generowanie szablonu GDDKiA".
export const GDDKIA_MAP_URL = "https://drogi.gddkia.gov.pl/mapy/mapa-informacji-drogowej";

const FIELDS: { key: keyof ApplicationRoadData; label: string; width?: string }[] = [
  { key: "road_number", label: "Nr drogi krajowej", width: "w-28" },
  { key: "relacja", label: "Odcinek / relacja", width: "w-56" },
  { key: "km_start", label: "km od", width: "w-20" },
  { key: "m_start", label: "m od", width: "w-20" },
  { key: "km_end", label: "km do", width: "w-20" },
  { key: "m_end", label: "m do", width: "w-20" },
  { key: "side", label: "Strona (lewa/prawa)", width: "w-36" },
];

/**
 * Dane drogi krajowej wniosku lokalizacyjnego GDDKiA: numer drogi, kilometraż
 * początku/końca zajęcia pasa i strona — propozycja z API mapy GDDKiA (przycisk),
 * każda wartość edytowalna ręcznie (zapis PATCH-em na blur). Zasilają zmienne
 * #numer_drogi_krajowej#, #relacja_dk#, #droga_kilometr_*#, #strona_lewa_prawa#
 * druku „zezwolenie na lokalizację w pasie drogowym”.
 */
export function RoadDataCard({
  app, editable, computing, suggesting, error,
  onCompute, onSuggestRelacja, onSave,
}: {
  app: OfficialApplicationDetail;
  editable: boolean;
  computing: boolean;
  suggesting: boolean;
  error: string;
  onCompute: () => void;
  onSuggestRelacja: () => void;
  onSave: (roadData: ApplicationRoadData) => void;
}) {
  const road = app.road_data ?? {};
  // Draft per pole (null = pokazuj wartość z API); zapis PATCH-em całego obiektu na blur.
  const [draft, setDraft] = useState<Partial<Record<string, string>>>({});

  const valueFor = (key: keyof ApplicationRoadData) =>
    draft[key] ?? (road[key] === undefined || road[key] === null ? "" : String(road[key]));

  const saveField = (key: keyof ApplicationRoadData) => {
    const value = draft[key];
    if (value === undefined) return;
    const current = road[key] === undefined || road[key] === null ? "" : String(road[key]);
    if (value === current) {
      setDraft((s) => ({ ...s, [key]: undefined }));
      return;
    }
    onSave({ ...road, [key]: value, source: "manual" });
    setDraft((s) => ({ ...s, [key]: undefined }));
  };

  return (
    <div className="space-y-1 border-t border-[var(--color-border)] pt-2">
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold flex items-center gap-1">
          <MapPin size={13} /> Dane drogi krajowej (GDDKiA)
          <InfoHint text="Numer drogi, kilometraż (pikietaż) początku i końca zajęcia pasa oraz strona drogi — do druku „zezwolenie na lokalizację w pasie drogowym”. Przycisk liczy propozycję z punktów referencyjnych mapy GDDKiA (słupki co 100 m) po przebiegu trasy przez działki wniosku; każdą wartość możesz poprawić ręcznie. Relację (odcinek) podpowiada przycisk AI przy polu — okno generowania pisma bierze wszystkie wartości stąd." />
        </span>
        <div className="flex items-center gap-2">
          <a
            href={GDDKIA_MAP_URL}
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline"
          >
            <ExternalLink size={12} /> Mapa informacji drogowej GDDKiA
          </a>
          <Button size="sm" variant="secondary" loading={computing} disabled={!editable} onClick={onCompute}>
            Pobierz kilometraż z mapy GDDKiA
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        {FIELDS.map((f) => (
          <label key={f.key} className="block">
            <span className="text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">{f.label}</span>
            <span className="flex items-center gap-1">
              <input
                className={`input-base mt-0.5 !py-1 ${f.width ?? ""}`}
                value={valueFor(f.key)}
                disabled={!editable}
                onChange={(e) => setDraft((s) => ({ ...s, [f.key]: e.target.value }))}
                onBlur={() => saveField(f.key)}
              />
              {/* Relacja mieszka TYLKO tutaj — okno generowania już jej nie pyta,
                  podstawia wartość z karty. AI podpowiada, człowiek akceptuje. */}
              {f.key === "relacja" && (
                <Button
                  size="sm" variant="secondary"
                  loading={suggesting}
                  disabled={!editable || !road.road_number}
                  title={road.road_number
                    ? "AI podpowie relację z numeru drogi, kilometrażu i okolicy działek"
                    : "Najpierw pobierz dane drogi z mapy GDDKiA"}
                  onClick={onSuggestRelacja}
                >
                  <Sparkles size={13} />
                </Button>
              )}
            </span>
          </label>
        ))}
      </div>
      {/* Podgląd do prostej weryfikacji: trasa + działki + słupki pikietażu
          z podpisami km na podkładzie OSM; zielone słupki = wyliczony zakres.
          Klik otwiera pełny rozmiar. */}
      {app.road_map_png?.url && (
        <a href={app.road_map_png.url} target="_blank" rel="noreferrer" className="block w-fit">
          <img
            src={app.road_map_png.url}
            alt="Podgląd danych drogi — trasa i słupki pikietażu GDDKiA"
            className="max-h-64 border border-[var(--color-border-strong)] hover:opacity-90"
          />
        </a>
      )}
      <div className="text-[var(--color-text-muted)]">
        {road.computed_at ? (
          <>
            Źródło: {road.source === "manual" ? "poprawione ręcznie" : "API GDDKiA"}
            {road.ref_road && road.ref_road !== road.road_number && (
              <> (linia referencyjna {road.ref_road} — tak podpisane są słupki na mapie GDDKiA)</>
            )}
            {" · "}zasila zmienne <span className="font-mono">#numer_drogi_krajowej#</span>,{" "}
            <span className="font-mono">#droga_kilometr_*#</span>, <span className="font-mono">#strona_lewa_prawa#</span>.
          </>
        ) : (
          <>Brak danych — pobierz z mapy GDDKiA albo wpisz ręcznie (pola zostaną puste w druku, dopóki ich nie uzupełnisz).</>
        )}
      </div>
      {error && <div className="text-amber-700">⚠ {error}</div>}
    </div>
  );
}
